/* ============================================================================
   Cáscara de la aplicación: navegación, estado compartido y pie.

   El estado que importa vive en la URL (#/vista?ambito=…) para que cualquier
   pantalla sea enlazable: un banco de alimentos puede mandar por correo la vista
   exacta que está viendo.
   ========================================================================== */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Chrome, type NavItem } from './components/Chrome'
import { Sheet } from './components/Sheet'
import { Panorama } from './views/Panorama'
import { Productos } from './views/Productos'
import { Despensa } from './views/Despensa'
import { Asequibilidad } from './views/Asequibilidad'
import { Territorio } from './views/Territorio'
import { Abasto } from './views/Abasto'
import { PrecioBase, precioBaseDefaults, type PrecioBaseFiltros } from './views/PrecioBase'
import { Fuentes } from './views/Fuentes'
import { EstadoDeDatos } from './views/EstadoDeDatos'
import { dataset, type Ambito } from './data/dataset'
import { formatPeriodTitle } from './lib/format'
import './views/views.css'

export type ViewId =
  | 'panorama'
  | 'productos'
  | 'territorio'
  | 'abasto'
  | 'precio-base'
  | 'despensa'
  | 'asequibilidad'
  | 'fuentes'

/* El arreglo ES el orden de las pestañas. «Precio base» va pegada a «Abasto»
   porque son el par operativo —Abasto dice a quién comprarle, Precio base a qué
   precio convocar la compra— y Despensa y Asequibilidad son la lectura por
   hogar, que es otra pregunta. Fuentes se queda al final, siempre. */
const NAV: readonly NavItem<ViewId>[] = [
  { id: 'panorama', label: 'Panorama' },
  { id: 'productos', label: 'Productos' },
  { id: 'territorio', label: 'Territorio' },
  { id: 'abasto', label: 'Abasto' },
  { id: 'precio-base', label: 'Precio base' },
  { id: 'despensa', label: 'Despensa' },
  { id: 'asequibilidad', label: 'Asequibilidad' },
  { id: 'fuentes', label: 'Fuentes' },
]

const VIEW_IDS = new Set<string>(NAV.map((item) => item.id))

/**
 * Los valores por omisión de «Precio base», derivados del dataset una sola vez.
 *
 * No se teclea ninguno: `precioBaseDefaults` los saca de `retail.references` y
 * de `retail.periods`, y esos cambian cada quincena que Profeco publica. Sirven
 * para OMITIRLOS de la URL, que es la regla de la casa: la URL limpia de la
 * vista tiene que seguir siendo `#/precio-base`.
 */
const PRECIO_BASE_OMISION = precioBaseDefaults(dataset.retail)

/** Los cuatro filtros vacíos: la vista resuelve cada uno contra el dato. */
const PRECIO_BASE_VACIO: PrecioBaseFiltros = {
  renglon: null,
  pres: null,
  entrega: null,
  mes: null,
}

interface Route {
  view: ViewId
  ambito: Ambito
  /** Sólo lo lee y lo escribe `precio-base`; las demás vistas lo ignoran. */
  precioBase: PrecioBaseFiltros
}

const DEFAULT_ROUTE: Route = {
  view: 'panorama',
  ambito: 'urbano',
  precioBase: PRECIO_BASE_VACIO,
}

/**
 * Sólo se interpreta como ruta lo que empieza con `#/`, que es como escribe
 * `writeHash`. Cualquier otro fragmento es un ancla de la página y NO debe tocar
 * el estado.
 *
 * Sin esta condición, el enlace «Ir al contenido» —el único control que existe
 * exclusivamente para quien navega con teclado o lector de pantalla— ponía
 * `#contenido` en la URL, el router no lo reconocía como vista y caía al default:
 * te devolvía a Panorama con ámbito urbano, cambiando en silencio la población
 * medida. El único atajo de accesibilidad era el único que destruía el estado.
 */
function parseHash(current: Route = DEFAULT_ROUTE): Route {
  const raw = typeof window === 'undefined' ? '' : window.location.hash
  if (raw === '' || raw === '#') return DEFAULT_ROUTE
  if (!raw.startsWith('#/')) return current

  const [path, query] = raw.slice(2).split('?')
  const params = new URLSearchParams(query ?? '')
  const view = path && VIEW_IDS.has(path) ? (path as ViewId) : 'panorama'
  const ambito = params.get('ambito') === 'rural' ? 'rural' : 'urbano'
  /* Los cuatro filtros de «Precio base» se leen crudos y no se validan aquí.
     `URLSearchParams.get` ya devuelve `string | null`, que es exactamente el
     tipo de `PrecioBaseFiltros`, y la validación la hace la vista, que es la
     única que puede: `entrega` sólo se puede comprobar contra las entidades con
     central, y ésas viven en `public/precio-base-2026.json`, un archivo de 1.3 MB
     que la vista baja bajo demanda y que el router no tiene ni debe tener.

     La consecuencia, medida: con `#/precio-base?renglon=A999` la vista cae en su
     valor por omisión y la URL conserva el `A999`. Se acepta porque la pantalla
     nunca miente —el renglón y el mes vigentes se imprimen en el selector y en la
     franja de vigencia— y porque quien reciba ese enlace verá exactamente lo
     mismo que quien lo mandó, que es para lo que existe el estado en la URL.
     Validar aquí lo que sí se puede (renglón, presentación, mes) y no `entrega`
     dejaría media regla en cada archivo, que se desincroniza antes. */
  const precioBase: PrecioBaseFiltros = {
    renglon: params.get('renglon'),
    pres: params.get('pres'),
    entrega: params.get('entrega'),
    mes: params.get('mes'),
  }
  return { view, ambito, precioBase }
}

/**
 * Cambiar de vista SÍ empuja una entrada al historial: el boton Atras del
 * navegador es la salida que la gente espera, y con replaceState quedaba
 * atrapada. Cambiar de ambito NO la empuja: es un ajuste dentro de la misma
 * vista y llenaria el historial de ruido.
 */
function writeHash(route: Route, push: boolean): void {
  const params = new URLSearchParams()
  if (route.ambito !== 'urbano') params.set('ambito', route.ambito)
  /* Sólo la vista que los usa los escribe. Arrastrarlos a `#/panorama` los
     convertiría en ruido que además sobrevive a un cambio de mes de Profeco:
     `mes=2026-05` copiado a otra pestaña deja de ser el último cuando llega
     junio, y la URL diría un mes que ya no es el vigente.

     Se omite lo que coincide con el valor por omisión. `pres` se compara contra
     la presentación por omisión del renglón por omisión: si alguien elige otro
     renglón y luego su primera presentación a mano, `pres` SÍ va en la URL,
     porque para esa ruta no es una omisión, es una elección. */
  if (route.view === 'precio-base') {
    const pb = route.precioBase
    const om = PRECIO_BASE_OMISION
    if (pb.renglon !== null && pb.renglon !== om.renglon) params.set('renglon', pb.renglon)
    if (pb.pres !== null && pb.pres !== om.pres) params.set('pres', pb.pres)
    if (pb.entrega !== null && pb.entrega !== om.entrega) params.set('entrega', pb.entrega)
    if (pb.mes !== null && pb.mes !== om.mes) params.set('mes', pb.mes)
  }
  const query = params.toString()
  const next = `#/${route.view}${query ? `?${query}` : ''}`
  if (window.location.hash === next) return
  if (push) window.history.pushState(null, '', next)
  else window.history.replaceState(null, '', next)
}

export function App() {
  const [route, setRoute] = useState<Route>(() => parseHash())
  const [showStatus, setShowStatus] = useState(false)

  /** Ruta vigente, para leerla desde manejadores sin re-suscribirlos. */
  const routeRef = useRef(route)
  routeRef.current = route

  useEffect(() => {
    /* `hashchange` cubre navegar escribiendo la URL; `popstate` cubre Atrás y
       Adelante. Los dos disparan al traversar entre URLs que sólo difieren en el
       fragmento, y el manejador es idempotente, así que escuchar ambos es más
       barato que depender de qué evento manda en cada navegador. */
    const sync = () => {
      const raw = window.location.hash
      const isAnchor = raw !== '' && raw !== '#' && !raw.startsWith('#/')
      if (isAnchor) {
        /* Era un ancla de página («#contenido», del enlace de salto): el salto ya
           ocurrió y la ruta no cambia. Devolvemos la URL a su forma compartible
           sin deshacer el desplazamiento ni ensuciar el historial. */
        writeHash(routeRef.current, false)
        return
      }
      setRoute((prev) => parseHash(prev))
    }
    window.addEventListener('hashchange', sync)
    window.addEventListener('popstate', sync)
    return () => {
      window.removeEventListener('hashchange', sync)
      window.removeEventListener('popstate', sync)
    }
  }, [])

  const lastView = useRef(route.view)
  useEffect(() => {
    const push = route.view !== lastView.current
    lastView.current = route.view
    writeHash(route, push)
  }, [route])

  const navigate = useCallback((view: ViewId) => {
    setRoute((prev) => {
      if (prev.view === view) return prev
      window.scrollTo({ top: 0, behavior: 'auto' })
      return { ...prev, view }
    })
  }, [])

  const setAmbito = useCallback((ambito: Ambito) => {
    setRoute((prev) => (prev.ambito === ambito ? prev : { ...prev, ambito }))
  }, [])

  /* Cambiar un filtro no toca `view`, así que el efecto de arriba escribe con
     `replaceState`: el botón Atrás sigue retrocediendo entre vistas y no entre
     renglones del selector, que son cuatro campos y se mueven mucho. Tampoco
     desplaza la página, a diferencia de `navigate`: el filtro que acabas de
     mover suele estar a media pantalla y saltar al inicio te lo esconde. */
  const setPrecioBase = useCallback((precioBase: PrecioBaseFiltros) => {
    setRoute((prev) => ({ ...prev, precioBase }))
  }, [])

  const status = useMemo(() => `Datos a ${formatPeriodTitle(dataset.latestPeriod).toLowerCase()}`, [])

  return (
    <div className="app">
      {/* No navega: mueve el foco. Escribir `#contenido` en la URL empujaba una
          entrada de historial por cada uso, así que el botón Atrás retrocedía
          entre saltos en vez de entre vistas. */}
      <a
        className="skip-link t-footnote"
        href="#contenido"
        onClick={(event) => {
          event.preventDefault()
          const main = document.getElementById('contenido')
          main?.focus({ preventScroll: true })
          main?.scrollIntoView({ block: 'start', behavior: 'auto' })
        }}
      >
        Ir al contenido
      </a>

      <Chrome
        items={NAV}
        current={route.view}
        onNavigate={navigate}
        status={status}
        statusTone={
          dataset.ingestion.results.some((r) => r.status === 'error') ? 'error' : 'ok'
        }
        onStatusClick={() => setShowStatus(true)}
      />

      <main className="page" id="contenido" tabIndex={-1}>
        <div
          role="tabpanel"
          id={`panel-${route.view}`}
          aria-labelledby={`tab-${route.view}`}
          className="stack stack--loose"
        >
          {route.view === 'panorama' ? (
            <Panorama ambito={route.ambito} onAmbitoChange={setAmbito} onNavigate={navigate} />
          ) : null}
          {route.view === 'productos' ? (
            <Productos ambito={route.ambito} onAmbitoChange={setAmbito} />
          ) : null}
          {/* No recibe ámbito: el INPC territorial no se publica por ámbito
              rural/urbano, y pasarle uno fingiría un filtro que no existe. */}
          {route.view === 'territorio' ? <Territorio /> : null}
          {/* Tampoco recibe ámbito: el directorio de mayoristas no se publica
              por rural/urbano y pasarle uno fingiría un filtro inexistente. */}
          {route.view === 'abasto' ? <Abasto /> : null}
          {/* Tercer caso sin ámbito, y el más claro de los tres: QQP se
              desglosa por canal y por entidad, no por rural/urbano. Pasarle un
              ámbito fingiría un filtro que no existe. */}
          {route.view === 'precio-base' ? (
            <PrecioBase filtros={route.precioBase} onFiltros={setPrecioBase} />
          ) : null}
          {route.view === 'despensa' ? (
            <Despensa ambito={route.ambito} onAmbitoChange={setAmbito} />
          ) : null}
          {route.view === 'asequibilidad' ? (
            <Asequibilidad ambito={route.ambito} onAmbitoChange={setAmbito} />
          ) : null}
          {route.view === 'fuentes' ? <Fuentes /> : null}
        </div>
      </main>

      <footer className="foot no-print">
        <div className="foot__inner">
          <div className="foot__block">
            <h2 className="t-title3">Monitor de la canasta alimentaria</h2>
            <p className="t-footnote ink-2">
              Seguimiento mensual del costo de la canasta alimentaria en México para la Red de Bancos
              de Alimentos de México. Toda cifra en esta herramienta proviene de una publicación
              oficial y lleva su fuente y su fecha de consulta.
            </p>
            <p className="t-caption ink-3">
              Última ingesta: {new Date(dataset.ingestion.runAt).toLocaleString('es-MX')} ·{' '}
              {dataset.series.length} series · {dataset.products.length} productos
            </p>
          </div>
          <div className="foot__block">
            <h2 className="t-title3">Fuentes</h2>
            <ul className="foot__list">
              {dataset.sources.map((source) => (
                <li key={source.id} className="t-footnote">
                  <a href={source.url} target="_blank" rel="noreferrer noopener">
                    {source.institution}
                  </a>
                  <span className="ink-3"> · {source.name}</span>
                </li>
              ))}
            </ul>
            <button
              type="button"
              className="foot__link t-footnote"
              onClick={() => setShowStatus(true)}
            >
              Ver estado de la última ingesta
            </button>
          </div>
        </div>
      </footer>

      <Sheet
        open={showStatus}
        onClose={() => setShowStatus(false)}
        title="Estado de los datos"
        subtitle={`Ingesta del ${new Date(dataset.ingestion.runAt).toLocaleString('es-MX')}`}
      >
        <EstadoDeDatos />
      </Sheet>
    </div>
  )
}
